import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Mail, Coins, ScrollText, MapPin } from "lucide-react";
import { SigilMark } from "../components/Sigil";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      navigate("/sanctum/login");
      return;
    }
    Promise.all([
      axios.get(`${API}/admin/enquiries`, { withCredentials: true }),
      axios.get(`${API}/admin/transactions`, { withCredentials: true }),
      axios.get(`${API}/admin/deliveries`, { withCredentials: true }),
    ])
      .then(([enq, txn, del]) => {
        setEnquiries(enq.data);
        setTransactions(txn.data);
        setDeliveries(del.data);
      })
      .catch(() => navigate("/sanctum/login"))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    navigate("/sanctum/login");
  };

  if (user === null || loading) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center text-gold">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  const paid = transactions.filter((t) => t.payment_status === "paid");
  const revenue = paid.reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-ink text-parchment">
      <header className="border-b border-gold/20 bg-ink/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 text-gold">
            <SigilMark className="w-8 h-8" />
            <span className="font-display tracking-[0.22em] text-sm">THE SANCTUM LEDGER</span>
          </div>
          <div className="flex items-center gap-5">
            <span data-testid="dashboard-user-email" className="hidden sm:block text-xs font-mono text-parchment-muted">
              {user.email}
            </span>
            <Link to="/" data-testid="dashboard-home-link" className="text-xs uppercase tracking-[0.2em] text-parchment-muted hover:text-gold transition-colors">
              View Site
            </Link>
            <button
              data-testid="dashboard-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-gold/50 text-gold rounded-full px-4 py-2 hover:bg-gold hover:text-ink transition-colors"
            >
              <LogOut size={14} /> Depart
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-5 mb-10">
          {[
            { label: "Petitions Received", value: enquiries.length, icon: Mail, tid: "stat-enquiries" },
            { label: "Paid Workings", value: paid.length, icon: ScrollText, tid: "stat-paid" },
            { label: "Destinations Sealed", value: deliveries.length, icon: MapPin, tid: "stat-deliveries" },
            { label: "Tribute Gathered", value: revenue.toLocaleString("en-IN"), icon: Coins, tid: "stat-revenue" },
          ].map((s) => (
            <div key={s.tid} data-testid={s.tid} className="parchment-card rounded-xl p-6 flex items-center gap-4">
              <s.icon className="text-gold" size={26} strokeWidth={1.5} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70">{s.label}</p>
                <p className="font-display text-3xl text-parchment mt-1">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          {[
            ["enquiries", "Petitions"],
            ["transactions", "Tribute Ledger"],
            ["deliveries", "Destinations"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`dashboard-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] transition-colors ${
                tab === key ? "bg-gold text-ink font-semibold" : "border border-gold/30 text-parchment-muted hover:text-gold"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div data-testid="dashboard-enquiries-list" className="space-y-4">
            {enquiries.length === 0 && (
              <p className="text-parchment-muted text-sm font-mono">No petitions yet. The ravens rest.</p>
            )}
            {enquiries.map((e) => (
              <article key={e.id} data-testid={`enquiry-row-${e.id}`} className="parchment-card rounded-xl p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <h3 className="font-serif text-lg text-gold">{e.name}</h3>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-parchment-muted">
                    {fmtDate(e.created_at)}
                  </span>
                </div>
                <p className="text-xs font-mono text-parchment-muted mb-1">{e.email}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] font-mono text-gold/70 mb-3">
                  Domain: {e.domain}{e.birth_info ? ` · Born: ${e.birth_info}` : ""}
                </p>
                <p className="text-sm text-parchment/85 leading-relaxed">{e.message}</p>
              </article>
            ))}
          </div>
        )}

        {tab === "transactions" && (
          <div data-testid="dashboard-transactions-list" className="overflow-x-auto parchment-card rounded-xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gold/20 text-left">
                  {["Package", "Amount", "Status", "Order", "Date"].map((h) => (
                    <th key={h} className="px-5 py-4 text-[10px] uppercase tracking-[0.25em] font-mono text-gold/70">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-parchment-muted font-mono text-sm">
                      The ledger is empty.
                    </td>
                  </tr>
                )}
                {transactions.map((t) => {
                  const ref = t.order_id || t.session_id || "";
                  return (
                  <tr key={ref} data-testid={`transaction-row-${ref}`} className="border-b border-gold/10 last:border-0">
                    <td className="px-5 py-4 text-parchment">{t.package_name || t.package_id}</td>
                    <td className="px-5 py-4 font-mono text-gold">
                      {t.currency === "inr" ? "₹" : "$"}{Number(t.amount).toLocaleString("en-IN")}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] uppercase tracking-[0.2em] font-mono rounded-full px-3 py-1 ${
                        t.payment_status === "paid"
                          ? "bg-gold/15 text-gold border border-gold/40"
                          : "border border-parchment-muted/30 text-parchment-muted"
                      }`}>
                        {t.payment_status}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-mono text-xs text-parchment-muted">{ref.slice(0, 22)}{ref.length > 22 ? "…" : ""}</td>
                    <td className="px-5 py-4 text-xs text-parchment-muted">{fmtDate(t.created_at)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "deliveries" && (
          <div data-testid="dashboard-deliveries-list" className="space-y-4">
            {deliveries.length === 0 && (
              <p className="text-parchment-muted text-sm font-mono">No destinations sealed yet.</p>
            )}
            {deliveries.map((d) => (
              <article key={d.order_id} data-testid={`delivery-row-${d.order_id}`} className="parchment-card rounded-xl p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <h3 className="font-serif text-lg text-gold">{d.package_name || "Working"}</h3>
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-parchment-muted">
                    {fmtDate(d.created_at)}
                  </span>
                </div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-mono text-gold/70 mb-3">
                  Order: {d.order_id} · By {d.method}
                </p>
                {d.method === "email" ? (
                  <p className="text-sm text-parchment/85">{d.email}</p>
                ) : (
                  <p className="text-sm text-parchment/85 leading-relaxed">
                    {d.full_name}<br />
                    {d.line1}{d.line2 ? <>, {d.line2}</> : null}<br />
                    {d.city}, {d.state} — {d.pincode}<br />
                    {d.country}
                  </p>
                )}
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
